/**
 * @file Button Component
 * @description Un componente de botón versátil basado en Radix UI con múltiples variantes y tamaños.
 * Soporta composición mediante Slot de Radix UI para mayor flexibilidad.
 */

import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority";

import { cn } from "../../lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        outline:
          "border border-input shadow-sm hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-10 rounded-md px-8",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

/**
 * Componente Button
 *
 * Un botón altamente personalizable con múltiples variantes y tamaños.
 *
 * @component
 * @example
 * ```jsx
 * // Botón primario básico
 * <Button>Click me</Button>
 *
 * // Botón secundario
 * <Button variant="secondary">Secondary</Button>
 *
 * // Botón destructivo pequeño
 * <Button variant="destructive" size="sm">Delete</Button>
 *
 * // Botón como enlace usando asChild
 * <Button asChild>
 *   <a href="/path">Link Button</a>
 * </Button>
 * ```
 *
 * @param {Object} props - Propiedades del componente
 * @param {string} [props.className] - Clases CSS adicionales
 * @param {('default'|'destructive'|'outline'|'secondary'|'ghost'|'link')} [props.variant='default'] - Variante visual del botón
 * @param {('default'|'sm'|'lg'|'icon')} [props.size='default'] - Tamaño del botón
 * @param {boolean} [props.asChild=false] - Si es true, el botón se renderiza como su hijo usando Radix Slot
 * @param {React.Ref} ref - Ref del elemento
 * @returns {React.ReactElement} Elemento Button renderizado
 */
const Button = React.forwardRef(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button"
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      {...props} />
  );
})
Button.displayName = "Button"

export { Button, buttonVariants }
